<template>
  <div>
    <!-- Filtro de Período -->
    <div class="mb-6 flex flex-wrap items-center justify-between gap-4">
      <FinancialPeriodFilter v-model="filters" @change="handleFilterChange" />

      <vs-button
        v-if="filters.institution_id"
        icon-pack="feather"
        icon="icon-external-link"
        :disabled="openingStripeDashboard"
        @click="openStripeDashboard">
        Abrir painel do Stripe
      </vs-button>
    </div>

    <!-- Cards de Indicadores -->
    <div v-if="loading" class="text-center py-8">
      <vs-button type="border" class="mb-2" :disabled="true">
        <span class="animate-spin">Carregando...</span>
      </vs-button>
    </div>

    <template v-else>
      <!-- Cards de Indicadores -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6"
      >
        <DashboardStatisticsCard
          title="Total de Receita"
          :value="dashboardData.total_revenue"
          description="Receita total confirmada"
          type="primary"
          icon="DollarSignIcon"
          icon-color="success"
          value-size="large"
          :formatter="formatCurrency"
        />

        <DashboardStatisticsCard
          title="Taxas da Plataforma"
          :value="dashboardData.platform_fees"
          description="Receita líquida = bruta − taxas da plataforma"
          type="info"
          icon="PercentIcon"
          value-size="small"
          :formatter="formatCurrency"
        />

        <DashboardStatisticsCard
          title="Ticket Médio"
          :value="dashboardData.avg_order_value"
          description="Valor médio por pedido"
          type="info"
          icon="TrendingUpIcon"
          value-size="small"
          :formatter="formatCurrency"
        />

        <DashboardStatisticsCard
          title="Receita Pendente"
          :value="dashboardData.pending_revenue"
          description="Aguardando confirmação"
          type="warning"
          icon="ClockIcon"
          value-size="small"
          :formatter="formatCurrency"
        />

        <DashboardStatisticsCard
          title="Total de Pedidos"
          :value="dashboardData.total_orders"
          description="Pedidos realizados"
          type="primary"
          icon="ShoppingBagIcon"
          value-size="large"
        />

        <DashboardStatisticsCard
          title="Pagamentos Pendentes"
          :value="dashboardData.pending_payment_count"
          description="Aguardando processamento"
          type="warning"
          icon="AlertCircleIcon"
          value-size="large"
        />

        <DashboardStatisticsCard
          title="Pagamentos Pagos"
          :value="dashboardData.paid_payment_count"
          description="Pagamentos confirmados"
          type="success"
          icon="CheckCircleIcon"
          value-size="large"
        />

        <DashboardStatisticsCard
          title="Pagamentos Recusados"
          :value="dashboardData.refused_payment_count"
          description="Pagamentos recusados"
          type="danger"
          icon="XCircleIcon"
          value-size="large"
        />
      </div>

      <!-- Gráficos -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Gráfico de Pizza - Distribuição de Status de Pagamentos -->
        <vx-card>
          <template slot="no-body">
            <div class="p-6">
              <h3 class="mb-4 font-semibold text-lg">Status de Pagamentos</h3>
              <vue-apex-charts
                v-if="
                  dashboardData &&
                    paymentStatusSeries &&
                    paymentStatusSeries.length > 0
                "
                type="donut"
                height="350"
                :options="paymentStatusChartOptions"
                :series="paymentStatusSeries"
              />
              <div
                v-else
                class="flex items-center justify-center h-64 text-gray-500"
              >
                Nenhum dado disponível
              </div>
            </div>
          </template>
        </vx-card>

        <!-- Gráfico de Barras - Comparação de Receita -->
        <vx-card>
          <template slot="no-body">
            <div class="p-6">
              <h3 class="mb-4 font-semibold text-lg">Comparação de Receita</h3>
              <vue-apex-charts
                type="bar"
                height="350"
                :options="revenueChartOptions"
                :series="revenueSeries"
              />
            </div>
          </template>
        </vx-card>
      </div>

      <!-- Top 3 Produtos Mais Vendidos -->
      <vx-card>
        <template slot="no-body">
          <div class="p-6">
            <h3 class="mb-4 font-semibold text-lg">
              Top 3 Produtos Mais Vendidos
            </h3>
            <vue-apex-charts
              type="bar"
              height="350"
              :options="topProductsChartOptions"
              :series="topProductsSeries"
            />
          </div>
        </template>
      </vx-card>
    </template>
  </div>
</template>

<script>
import FinancialPeriodFilter from '@/components/filters/FinancialPeriodFilter.vue'
import DashboardStatisticsCard from '@/components/statistics-cards/DashboardStatisticsCard.vue'
import FinancialDashboardService from '@/services/api/FinancialDashboardService'
import StripeConnectService from '@/services/api/StripeConnectService'
import { currencyFormatter } from '@/util/Util'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    FinancialPeriodFilter,
    DashboardStatisticsCard,
    VueApexCharts
  },
  data() {
    return {
      service: null,
      filters: {
        institution_id: null,
        start_date: null,
        end_date: null
      },
      dashboardData: {
        total_revenue: 0,
        gross_revenue: 0,
        net_revenue: 0,
        pending_revenue: 0,
        total_orders: 0,
        avg_order_value: 0,
        // Status de pagamento (payment.status se tiver, senão order.status)
        pending_count: 0,
        processing_count: 0,
        paid_count: 0,
        refused_count: 0,
        canceled_count: 0,
        // Status dos pagamentos (para compatibilidade)
        pending_payment_count: 0,
        paid_payment_count: 0,
        refused_payment_count: 0,
        top_products: []
      },
      loading: false,
      stripeConnectService: null,
      openingStripeDashboard: false
    }
  },
  computed: {
    // Dados para gráfico de pizza - Status de Pagamento (payment.status ou order.status)
    paymentStatusSeries() {
      if (!this.dashboardData) {
        return [0, 0, 0, 0, 0]
      }

      const pending = Number(this.dashboardData.pending_count) || 0
      const processing = Number(this.dashboardData.processing_count) || 0
      const paid = Number(this.dashboardData.paid_count) || 0
      const refused = Number(this.dashboardData.refused_count) || 0
      const canceled = Number(this.dashboardData.canceled_count) || 0

      // Verificar se há pelo menos um valor maior que zero
      if (
        pending === 0 &&
        processing === 0 &&
        paid === 0 &&
        refused === 0 &&
        canceled === 0
      ) {
        return [0, 0, 0, 0, 0]
      }

      return [pending, processing, paid, refused, canceled]
    },
    paymentStatusChartOptions() {
      const pending = Number(this.dashboardData.pending_count) || 0
      const processing = Number(this.dashboardData.processing_count) || 0
      const paid = Number(this.dashboardData.paid_count) || 0
      const refused = Number(this.dashboardData.refused_count) || 0
      const canceled = Number(this.dashboardData.canceled_count) || 0
      const total = pending + processing + paid + refused + canceled

      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: [
          'Pendente',
          'Em Processamento',
          'Pago',
          'Recusado',
          'Cancelado'
        ],
        colors: ['#ff9f43', '#7367f0', '#28c76f', '#ea5455', '#82868b'],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          formatter: (seriesName, opts) => {
            const value = opts.w.globals.series[opts.seriesIndex] || 0
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
            return `${seriesName}: ${value} (${percentage}%)`
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }
        ],
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            // O val já vem como porcentagem no ApexCharts para gráficos donut/pie
            return Number(val).toFixed(1) + '%'
          },
          style: {
            fontSize: '14px',
            fontWeight: 'bold'
          }
        },
        tooltip: {
          y: {
            formatter: val => {
              const open = Number(this.dashboardData.open_count) || 0
              const done = Number(this.dashboardData.done_count) || 0
              const pending = Number(this.dashboardData.pending_count) || 0
              const paid = Number(this.dashboardData.paid_count) || 0
              const canceled = Number(this.dashboardData.canceled_count) || 0
              const total = open + done + pending + paid + canceled
              const percentage =
                total > 0 ? ((Number(val) / total) * 100).toFixed(1) : '0.0'
              return `${val} pedido${val !== 1 ? 's' : ''} (${percentage}%)`
            }
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    const pending =
                      Number(this.dashboardData.pending_count) || 0
                    const processing =
                      Number(this.dashboardData.processing_count) || 0
                    const paid = Number(this.dashboardData.paid_count) || 0
                    const refused =
                      Number(this.dashboardData.refused_count) || 0
                    const canceled =
                      Number(this.dashboardData.canceled_count) || 0
                    const total =
                      pending + processing + paid + refused + canceled
                    return total.toString()
                  },
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              }
            }
          }
        }
      }
    },
    // Dados para gráfico de barras - Comparação de Receita
    revenueSeries() {
      return [
        {
          name: 'Receita Bruta',
          data: [
            Math.round(
              (this.dashboardData.gross_revenue ||
                this.dashboardData.total_revenue) * 100
            ) / 100
          ]
        },
        {
          name: 'Receita Líquida',
          data: [Math.round((this.dashboardData.net_revenue || 0) * 100) / 100]
        }
      ]
    },
    revenueChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 8,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: val => {
            return this.formatCurrency(val)
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#304758']
          }
        },
        xaxis: {
          categories: ['Comparação'],
          labels: {
            style: {
              fontSize: '14px'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: val => {
              return this.formatCurrency(val)
            },
            style: {
              fontSize: '12px'
            }
          }
        },
        colors: ['#28c76f', '#00cfe8'], // Verde para Receita Bruta, Azul claro para Receita Líquida
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: val => {
              return this.formatCurrency(val)
            }
          }
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 4
        }
      }
    },
    // Dados para gráfico de barras - Top 3 Produtos
    topProductsSeries() {
      return [
        {
          name: 'Quantidade Vendida',
          data: this.dashboardData.top_products.map(p => p.total_quantity)
        },
        {
          name: 'Receita Total',
          data: this.dashboardData.top_products.map(
            p => Math.round(p.total_revenue * 100) / 100
          )
        }
      ]
    },
    topProductsChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 8,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          formatter: (val, opts) => {
            // Se for a série de Receita Total (índice 1), formatar como moeda
            if (opts.seriesIndex === 1) {
              return this.formatCurrency(val)
            }
            // Se for a série de Quantidade (índice 0), mostrar apenas o número
            return val
          },
          style: {
            fontSize: '12px',
            colors: ['#304758']
          }
        },
        xaxis: {
          categories: this.dashboardData.top_products.map(
            p => p.name || `Produto #${p.id}`
          ),
          labels: {
            style: {
              fontSize: '12px'
            },
            rotate: -45,
            rotateAlways: false
          }
        },
        yaxis: [
          {
            title: {
              text: 'Quantidade',
              style: {
                fontSize: '14px'
              }
            },
            labels: {
              style: {
                fontSize: '12px'
              }
            }
          },
          {
            opposite: true,
            title: {
              text: 'Receita (R$)',
              style: {
                fontSize: '14px'
              }
            },
            labels: {
              formatter: val => {
                return this.formatCurrency(val)
              },
              style: {
                fontSize: '12px'
              }
            }
          }
        ],
        colors: ['#7367f0', '#28c76f'],
        fill: {
          opacity: 1
        },
        legend: {
          position: 'top',
          fontSize: '14px'
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return val + ' unidade' + (val !== 1 ? 's' : '')
              } else {
                return this.formatCurrency(val)
              }
            }
          }
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 4
        }
      }
    }
  },
  methods: {
    formatCurrency(value) {
      return currencyFormatter(value)
    },
    openStripeDashboard() {
      if (!this.filters.institution_id || this.openingStripeDashboard) return

      // Abre a aba já na hora do clique (síncrono) para o navegador não
      // bloquear o popup — o link real é preenchido quando a resposta chega.
      const tab = window.open('', '_blank')
      this.openingStripeDashboard = true

      this.stripeConnectService
        .getLoginLink(this.filters.institution_id)
        .then(({ url }) => {
          if (tab) tab.location.href = url
        })
        .catch(error => {
          if (tab) tab.close()
          this.showErrors(null, error, this)
        })
        .finally(() => {
          this.openingStripeDashboard = false
        })
    },
    handleFilterChange(filters) {
      this.filters = filters
      this.loadDashboard()
    },
    loadDashboard() {
      this.loading = true
      this.$vs.loading()

      // Formatar custom_filters como objeto associativo (como o Grid faz)
      const customFilters = {}
      if (this.filters.institution_id) {
        customFilters.institution_id = this.filters.institution_id
      }
      if (this.filters.start_date) {
        customFilters.start_date = this.filters.start_date
      }
      if (this.filters.end_date) {
        customFilters.end_date = this.filters.end_date
      }

      this.service.getDashboard(customFilters).then(
        response => {
          this.dashboardData = response
          this.$vs.loading.close()
          this.loading = false
        },
        error => {
          this.$vs.loading.close()
          this.loading = false
          this.$vs.notify({
            title: 'Erro',
            text: 'Não foi possível carregar os dados do dashboard.',
            color: 'danger',
            iconPack: 'feather',
            icon: 'icon-alert-circle'
          })
        }
      )
    }
  },
  beforeMount() {
    this.service = FinancialDashboardService.build(this.$vs)
    this.stripeConnectService = StripeConnectService.build(this.$vs)
    this.loadDashboard()
  }
}
</script>

